/**
 * 全站倒计时组件
 * 支持格式：YYYY-MM-DD HH:mm (24小时制)
 */
class GlobalCountdown {
  constructor() {
    this.container = document.getElementById("GlobalCountdown");
    this.campaignAnnouncements = Array.from(
      document.querySelectorAll?.("[data-cz-campaign-announcement]") || []
    );
    this.startTime = null;
    this.endTime = null;
    this.timer = null;
    this.startDelayTimer = null;
    this.announcementTimer = null;
    this.offerTimer = null;
    this.clearanceObserver = null;
    this.isMobile = false;
    this.isSmall = false;
    this.realClockStartedAt = Date.now();
    this.previewClockBase = this.getPreviewClockBase();

    this.syncCampaignAnnouncement();

    if (this.container) {
      this.isSmall = this.container.classList.contains("global-countdown-container--small");
      this.isMobile = this.checkIsMobile();
      this.init();
    }
  }

  /**
   * Allow an explicit local-only review link to show the real component mid-campaign.
   * This never changes the browser's global clock or the configured campaign dates.
   */
  getPreviewClockBase() {
    const hostname = window.location?.hostname || "";
    if (hostname !== "localhost" && hostname !== "127.0.0.1") return null;

    const params = new URLSearchParams(window.location.search || "");
    if (params.get("early_bird_countdown") !== "preview") return null;

    return Date.parse("2026-11-13T20:00:00Z"); // November 13, noon Pacific Time.
  }

  getNow() {
    const realNow = Date.now();
    if (this.previewClockBase === null) return realNow;
    return this.previewClockBase + (realNow - this.realClockStartedAt);
  }

  /**
   * Keep campaign announcements useful after the promotion expires.
   * The countdown may be disabled, so this lifecycle is intentionally independent.
   */
  syncCampaignAnnouncement() {
    if (!this.campaignAnnouncements.length) return;

    let nextEndTime = null;
    this.campaignAnnouncements.forEach((campaignAnnouncement) => {
      const endTime = this.parseEndTime(campaignAnnouncement.dataset.endTime || "");
      const isEnded = !endTime || this.getNow() >= endTime.getTime();
      const text = isEnded
        ? campaignAnnouncement.dataset.endedText
        : campaignAnnouncement.dataset.activeText;
      const announcementTarget = campaignAnnouncement.querySelector?.("a") || campaignAnnouncement;

      campaignAnnouncement.hidden = isEnded && !text;
      if (text) announcementTarget.textContent = text;
      if (isEnded && text) announcementTarget.setAttribute("title", "Current sale offers");
      if (!isEnded && endTime && (!nextEndTime || endTime < nextEndTime)) nextEndTime = endTime;
    });

    const announcementBar = this.campaignAnnouncements[0].closest?.("announcement-bar");
    if (announcementBar?.setCurrentAnnouncement) {
      announcementBar.setCurrentAnnouncement(announcementBar.currentAnnouncement || 0);
      announcementBar.playAnnouncements?.();
    }

    clearTimeout(this.announcementTimer);
    this.announcementTimer = null;
    if (nextEndTime) {
      const remaining = nextEndTime.getTime() - this.getNow();
      this.announcementTimer = setTimeout(
        () => this.syncCampaignAnnouncement(),
        Math.min(remaining, 2147483647)
      );
    }
  }

  /**
   * Rotate complete offers on small mobile screens without growing the bar.
   */
  startOfferRotation() {
    const offers = this.container.querySelectorAll(".global-countdown__offer");
    if (!this.isSmall || offers.length < 2) return;

    let currentOffer = 0;
    this.offerTimer = setInterval(() => {
      if (!window.matchMedia("(max-width: 768px)").matches ||
          document.hidden || this.container.matches(":focus-within")) return;

      offers[currentOffer].classList.remove("is-active");
      currentOffer = (currentOffer + 1) % offers.length;
      offers[currentOffer].classList.add("is-active");
    }, 4000);
  }

  /**
   * 初始化倒计时
   */
  init() {
    // 从HTML数据属性或全局变量获取开始/结束时间
    const startTimeStr = this.getStartTimeFromSettings();
    const endTimeStr = this.getEndTimeFromSettings();

    if (!endTimeStr) {
      this.onCountdownExpired();
      console.warn("Global countdown: 未设置结束时间");
      return;
    }

    this.endTime = this.parseEndTime(endTimeStr);

    if (!this.endTime) {
      this.onCountdownExpired();
      console.error("Global countdown: 时间格式错误，请使用 YYYY-MM-DD HH:mm 格式");
      return;
    }

    if (startTimeStr) {
      this.startTime = this.parseEndTime(startTimeStr);
      if (!this.startTime || this.startTime >= this.endTime) {
        this.onCountdownExpired();
        console.error("Global countdown: 开始时间格式错误或不早于结束时间");
        return;
      }
    }

    if (this.startTime && this.getNow() < this.startTime.getTime()) {
      this.scheduleStart();
      return;
    }

    this.activateCountdown();
  }

  activateCountdown() {
    this.stopStartTimer();
    if (this.updateDisplay()) {
      this.container.style.display = "";
      this.container.style.visibility = "visible";
      document.documentElement.classList.add("global-countdown-active");
      this.syncClearance();
      if (!this.clearanceObserver && window.ResizeObserver) {
        this.clearanceObserver = new ResizeObserver(() => this.syncClearance());
        this.clearanceObserver.observe(this.container);
      }
      this.startTimer();
      this.startOfferRotation();
    }
  }

  scheduleStart() {
    document.documentElement.classList.remove("global-countdown-active");
    this.clearClearance();
    this.container.style.display = "none";
    this.container.style.visibility = "hidden";
    this.stopStartTimer();

    const remaining = this.startTime.getTime() - this.getNow();
    if (remaining <= 0) {
      this.activateCountdown();
      return;
    }

    // Browser timeouts are capped at roughly 24.8 days; re-check longer windows safely.
    this.startDelayTimer = setTimeout(() => this.scheduleStart(), Math.min(remaining, 2147483647));
  }

  syncClearance() {
    if (!this.container || !document.documentElement.classList.contains("global-countdown-active")) return;
    document.documentElement.style.setProperty(
      "--global-countdown-height",
      `${Math.ceil(this.container.getBoundingClientRect().height)}px`
    );
  }

  clearClearance() {
    this.clearanceObserver?.disconnect();
    this.clearanceObserver = null;
    document.documentElement.style.removeProperty("--global-countdown-height");
  }

  /**
   * 判断是否是手机端
   */
  checkIsMobile(threshold = 768) {
    return window.innerWidth < threshold;
  }

  /**
   * 从Shopify设置中获取结束时间
   * 这里需要从Liquid模板传递的数据中获取
   */
  getEndTimeFromSettings() {
    // 尝试从容器的data属性获取
    if (this.container.dataset.endTime) {
      return this.container.dataset.endTime;
    }

    // 尝试从全局变量获取（需要在Liquid模板中设置）
    if (window.globalCountdownEndTime) {
      return window.globalCountdownEndTime;
    }

    return null;
  }

  getStartTimeFromSettings() {
    if (this.container.dataset.startTime) {
      return this.container.dataset.startTime;
    }

    if (window.globalCountdownStartTime) {
      return window.globalCountdownStartTime;
    }

    return null;
  }

  /**
   * 解析时间字符串 (YYYY-MM-DD HH:mm)
   * @param {string} timeStr - 时间字符串
   * @returns {Date|null} - 解析后的Date对象或null
   */
  parseEndTime(timeStr) {
    // 验证格式：YYYY-MM-DD HH:mm
    const regex = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/;
    const match = timeStr.match(regex);

    if (!match) {
      return null;
    }

    const parts = match.slice(1).map(Number);
    const [year, month, day, hour, minute] = parts;
    const wallTime = Date.UTC(year, month - 1, day, hour, minute);
    const wallDate = new Date(wallTime);
    if (wallDate.getUTCFullYear() !== year || wallDate.getUTCMonth() !== month - 1 ||
        wallDate.getUTCDate() !== day || hour > 23 || minute > 59) return null;

    // Resolve Pacific wall time to an absolute instant, independent of the visitor's timezone.
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles", hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
    let instant = wallTime;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const values = Object.fromEntries(formatter.formatToParts(new Date(instant))
        .filter((part) => part.type !== "literal").map((part) => [part.type, Number(part.value)]));
      const observed = Date.UTC(values.year, values.month - 1, values.day, values.hour, values.minute, values.second);
      if (observed === wallTime) return new Date(instant);
      instant += wallTime - observed;
    }
    return null; // A nonexistent local time during the spring DST transition.
  }

  /**
   * 计算剩余时间
   * @returns {Object} - 包含天、时、分、秒的对象
   */
  calculateTimeRemaining() {
    const timeDiff = this.endTime.getTime() - this.getNow();

    // 如果时间已过，返回全零
    if (timeDiff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      };
    }

    // 计算各个时间单位
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      isExpired: false,
    };
  }

  /**
   * 更新显示
   */
  updateDisplay() {
    const timeRemaining = this.calculateTimeRemaining();

    // 如果倒计时结束
    if (timeRemaining.isExpired) {
      this.onCountdownExpired();
      return false;
    }

    // 更新各个时间单位的显示
    this.updateTimeUnit("day", timeRemaining.days);
    this.updateTimeUnit("hour", timeRemaining.hours);
    this.updateTimeUnit("minute", timeRemaining.minutes);
    this.updateTimeUnit("second", timeRemaining.seconds);
    return true;
  }

  /**
   * 更新单个时间单位的显示
   * @param {string} unit - 时间单位 (day, hour, minute, second)
   * @param {number} value - 时间值
   */
  updateTimeUnit(unit, value) {
    const element = this.container.querySelector(`.countdown__${unit} .countdown-item__value`);
    if (element) {
      // 确保显示两位数
      const padded = value.toString().padStart(2, "0");
      element.textContent = this.isMobile && unit === "day" && !this.isSmall
        ? `${value}DAYS`
        : padded;
    }
  }

  /**
   * 启动定时器
   */
  startTimer() {
    // 清除现有定时器
    if (this.timer) {
      clearInterval(this.timer);
    }

    // 每秒更新一次
    this.timer = setInterval(() => {
      this.updateDisplay();
    }, 1000);
  }

  /**
   * 停止定时器
   */
  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  stopStartTimer() {
    if (this.startDelayTimer) {
      clearTimeout(this.startDelayTimer);
      this.startDelayTimer = null;
    }
  }

  /**
   * 倒计时结束时的处理
   */
  onCountdownExpired() {
    document.documentElement.classList.remove("global-countdown-active");
    this.clearClearance();
    clearInterval(this.offerTimer);
    this.offerTimer = null;

    // 停止定时器
    this.stopStartTimer();
    this.stopTimer();

    // 隐藏倒计时组件
    if (this.container) {
      this.container.style.display = "none";
    }

    this.syncCampaignAnnouncement();

    // 可以在这里添加其他结束后的逻辑
    // 比如显示促销结束消息、重定向等
  }

  /**
   * 销毁倒计时实例
   */
  destroy() {
    this.stopStartTimer();
    this.stopTimer();
    clearInterval(this.offerTimer);
    clearTimeout(this.announcementTimer);
    this.clearClearance();
  }
}

// 当DOM加载完成后初始化倒计时
document.addEventListener("DOMContentLoaded", function () {
  // 创建全局倒计时实例
  window.globalCountdownInstance = new GlobalCountdown();
});

// 页面卸载时清理定时器
window.addEventListener("beforeunload", function () {
  if (window.globalCountdownInstance) {
    window.globalCountdownInstance.destroy();
  }
});
